/**
 * Vera Proposal route tree, composable under any parent path.
 *
 * The Vera Shell mounts it at `/proposal/*` via `rfpRoutes('/proposal')`, and
 * redirects the old `/rfp/*` to it. The factory prefixes sidebar `<Link>`s
 * with the mount path; route-object paths stay relative so React Router
 * resolves them against the mount point.
 *
 * Named RFP until 2026-08-07; the rename was display + route only, so every
 * identifier here (the package, `rfpRoutes`, `appId`, the `Rfp` role prefix,
 * `/api/rfp/*`) deliberately still says rfp.
 *
 * Vera Proposal is the first Postgres-backed Vera module — all of its data
 * access is BFF-mediated (`/api/rfp/*`), not Dataverse. See
 * docs/design/rfp/PROJECT_SPEC.md for the full design.
 */

import { lazy, Suspense, createContext, useContext } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import { AppShell, type NavItem } from '@widgets/app-shell';
import { ErrorBoundary } from '@shared/ui';
import { ProtectedRoute, isModuleManager } from '@shared/auth';
import { useAuth } from '@shared/providers/AuthProvider';
import {
  BoardRegular,
  ClipboardTaskListLtrRegular,
  BookRegular,
  MailInboxRegular,
  ScalesRegular,
  HistoryRegular,
  SettingsRegular,
  TaskListSquareLtrRegular,
} from '@fluentui/react-icons';

const DashboardPage = lazy(() => import('@rfp/pages/dashboard').then(m => ({ default: m.DashboardPage })));
const IntakePage = lazy(() => import('@rfp/pages/intake').then(m => ({ default: m.IntakePage })));
const ReviewPage = lazy(() => import('@rfp/pages/review').then(m => ({ default: m.ReviewPage })));
const ProjectsPage = lazy(() => import('@rfp/pages/projects').then(m => ({ default: m.ProjectsPage })));
const ProjectRecordPage = lazy(() => import('@rfp/pages/project-record').then(m => ({ default: m.ProjectRecordPage })));
const TasksPage = lazy(() => import('@rfp/pages/tasks').then(m => ({ default: m.TasksPage })));
const HistoryPage = lazy(() => import('@rfp/pages/history').then(m => ({ default: m.HistoryPage })));
const LibraryPage = lazy(() => import('@rfp/pages/library').then(m => ({ default: m.LibraryPage })));
const AdminPage = lazy(() => import('@rfp/pages/admin').then(m => ({ default: m.AdminPage })));

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>{children}</Suspense>;
}

/**
 * Mount-point prefix for every absolute `<Link>` inside the Proposal tree.
 * Empty string standalone; `/proposal` when mounted in the shell.
 */
const RfpBasePathContext = createContext<string>('');

/** Pages use this to build absolute links (e.g. `${base}/projects/${id}`). */
export function useRfpBasePath(): string {
  return useContext(RfpBasePathContext);
}

function buildNavItems(basePath: string, manager: boolean): NavItem[] {
  const p = (suffix: string) => `${basePath}${suffix}` || '/';
  const items: NavItem[] = [
    { path: p('/'), label: 'Dashboard', icon: BoardRegular },
    { path: p('/intake'), label: 'Intake', icon: MailInboxRegular },
    { path: p('/review'), label: 'Go / no-go', icon: ScalesRegular },
    { path: p('/projects'), label: 'Projects', icon: ClipboardTaskListLtrRegular },
    { path: p('/tasks'), label: 'Tasks', icon: TaskListSquareLtrRegular },
    { path: p('/history'), label: 'History', icon: HistoryRegular },
    { path: p('/library'), label: 'Answer Library', icon: BookRegular },
  ];
  // Admin is templates and settings, Manager and up. Hidden for the rest
  // rather than shown as a dead end; the page keeps its own gate for a typed URL.
  if (manager) items.push({ path: p('/admin'), label: 'Admin', icon: SettingsRegular });
  return items;
}

export function RfpRootLayout() {
  const basePath = useContext(RfpBasePathContext);
  const { roles } = useAuth();
  const navItems = buildNavItems(basePath, isModuleManager(roles, 'Rfp'));
  return (
    <ProtectedRoute appRolePrefix="Rfp">
      <ErrorBoundary>
        <AppShell
          appId="rfp"
          appTitle="Vera Proposal"
          navItems={navItems}
        >
          <Outlet />
        </AppShell>
      </ErrorBoundary>
    </ProtectedRoute>
  );
}

/**
 * Returns the Proposal route tree, with sidebar links prefixed for the mount
 * path. Standalone: `rfpRoutes()` at repo root. Shell: `rfpRoutes('/proposal')`.
 */
export function rfpRoutes(basePath: string = ''): RouteObject[] {
  return [
    {
      element: (
        <RfpBasePathContext.Provider value={basePath}>
          <RfpRootLayout />
        </RfpBasePathContext.Provider>
      ),
      children: [
        { index: true, element: <LazyPage><DashboardPage /></LazyPage> },
        { path: 'intake', element: <LazyPage><IntakePage /></LazyPage> },
        { path: 'review', element: <LazyPage><ReviewPage /></LazyPage> },
        { path: 'projects', element: <LazyPage><ProjectsPage /></LazyPage> },
        { path: 'projects/:requestId', element: <LazyPage><ProjectRecordPage /></LazyPage> },
        { path: 'tasks', element: <LazyPage><TasksPage /></LazyPage> },
        { path: 'history', element: <LazyPage><HistoryPage /></LazyPage> },
        { path: 'library', element: <LazyPage><LibraryPage /></LazyPage> },
        { path: 'admin', element: <LazyPage><AdminPage /></LazyPage> },
        { path: '*', element: <Navigate to={basePath || '/'} replace /> },
      ],
    },
  ];
}
